





































































































































import { Component, Vue } from 'vue-property-decorator';
import PublicQuestionSearch from '@/components/public-questions/PublicQuestionSearch.vue';
import PublicQuestionContainer from '@/components/public-questions/PublicQuestionContainer.vue';
import PublicQuestionFilter from '@/components/public-questions/PublicQuestionFilter.vue';
import QuestionLoading from '@/components/questions/QuestionLoading.vue';
import PublicQuestionSignup from '@/components/public-questions/PublicQuestionSignup.vue';
import SubjectTitleLoading from '@/components/subjects/SubjectTitleLoading.vue';
import SubjectNotFound from '@/components/subjects/SubjectNotFound.vue';
import NotFound from '@/components/common/NotFound.vue';
import {
  Subject,
  SubjectType,
  SubjectTypeFriendlyValue,
} from '@/resource/subject/subject.model';
import QuestionStore, {
  QuestionStoreInterface,
} from '@/resource/question/questions.store';
import SubjectStore, {
  SubjectStoreInterface,
} from '@/resource/subject/subject.store';
import { getToken } from '@/utils/cookie';
import ModalRegister from '@/components/modals/ModalRegister.vue';
import getEnumValue from '@/utils/common/get-enum-value';
import getFormByLevel from '@/utils/common/get-form-by-label';
import { MetaInfo } from 'vue-meta/types/vue-meta';
import seoDefault from '@/utils/seo-default';

export interface QuestionFilters {
  chapterIds: string[];
  languages: string[];
  difficulties: string[];
  keyword: string;
  sortBy: string;
}

@Component({
  components: {
    PublicQuestionContainer,
    PublicQuestionSearch,
    PublicQuestionFilter,
    QuestionLoading,
    PublicQuestionSignup,
    SubjectTitleLoading,
    SubjectNotFound,
    NotFound,
    ModalRegister,
  },
  metaInfo(this: SubjectView): MetaInfo {
    return {
      title: this.metaTitle,
      meta: [
        ...seoDefault({
          title: this.metaTitle + ' Mysoalan',
          description: this.metaDescription,
        }),
      ],
    };
  },
})
export default class SubjectView extends Vue {
  questionStore: QuestionStoreInterface = QuestionStore;
  subjectStore: SubjectStoreInterface = SubjectStore;

  showFilter = false;
  showPromoRegister = false;
  showMoveToTopButton = false;

  isLoadingQuestions = false;
  isLoadingSubject = false;
  isLoadingAddQuestions = false;

  isLoggedin = getToken() ? true : false;

  subject: Subject | null = null;
  selectedLanguages = ['english'];

  questionFilters: QuestionFilters = {
    chapterIds: [],
    languages: ['english'],
    difficulties: ['1', '2', '3'],
    keyword: '',
    sortBy: 'chapter-asc',
  };

  get subjectUuid() {
    return this.$route.params.uuid;
  }

  get subjectType() {
    const typeQuery = this.$route.params.type;

    if (Object.values(SubjectType).includes(typeQuery as any)) {
      return typeQuery as SubjectType;
    } else {
      return null;
    }
  }

  get subjectTypeTitle() {
    if (this.subjectType) {
      return getEnumValue(this.subjectType, SubjectTypeFriendlyValue);
    } else {
      return SubjectTypeFriendlyValue['lower-form'];
    }
  }

  get questions() {
    return this.questionStore.questions;
  }

  get isSubjective(): boolean {
    const isSubjective = this.$route.query['is-subjective'];
    if (isSubjective && typeof isSubjective === 'string') {
      return isSubjective.toLowerCase() === 'true';
    } else {
      return false;
    }
  }

  get isPromo(): boolean {
    const isPromo = this.$route.query['promo'];
    if (isPromo && typeof isPromo === 'string') {
      return isPromo.toLowerCase() === 'true';
    } else {
      return false;
    }
  }

  get questionType() {
    if (this.isSubjective) {
      return 'subjective questions';
    } else {
      return 'objective questions';
    }
  }

  get forms() {
    return this.subject ? this.subject.forms : [];
  }

  get initialChapters(): string[] {
    if (typeof this.$route.query.chapters === 'string') {
      return this.$route.query.chapters.split(',');
    } else {
      return [];
    }
  }

  get showSignin() {
    return (
      this.questionStore.pagination.page >=
      this.questionStore.pagination.totalPages
    );
  }

  get availableLanguages() {
    return this.subject?.languages as string[];
  }

  get levelQuery() {
    let levelQuery = '';
    if (typeof this.$route.query['level'] === 'string') {
      levelQuery = this.$route.query['level'];
    }
    return levelQuery ? getFormByLevel(levelQuery, '_') : null;
  }

  get metaTitle() {
    if (this.subject) {
      const questionType = this.isSubjective ? 'subjective' : 'objective';

      return (
        this.subject.name +
        ' ' +
        this.subjectTypeTitle +
        ' ' +
        questionType +
        ' questions' +
        ' |'
      );
    } else {
      return 'Question |';
    }
  }

  get metaDescription() {
    if (this.subject) {
      const questionType = this.isSubjective ? 'subjective' : 'objective';

      let school = 'high school';

      if (this.subjectType === SubjectType.Elementary) {
        school = 'school';
      }

      return (
        'List of ' +
        questionType +
        ' questions for ' +
        this.subject.name +
        ' ' +
        this.subjectTypeTitle +
        ' ' +
        school +
        '. Start adding all these questions to your collections.'
      );
    } else {
      return 'Subjects are the place where you can explore thousands of questions for any subject. You can filter questions by chapters, difficulties, languages, and more!';
    }
  }

  async created() {
    if (this.isPromo && !this.isLoggedin) {
      setTimeout(() => {
        this.showPromoRegister = true;
      }, 2000);
    }

    if (this.isLoggedin) {
      this.questionStore.changeIsPublic(false);
    } else {
      this.questionStore.changeIsPublic(true);
    }
    this.questionStore.changeIsSubjective(this.isSubjective);

    if (this.subjectUuid) {
      await this.firstFetch();
      window.addEventListener('scroll', this.scrollBottom);
    }
  }

  mounted() {
    window.addEventListener('resize', this.listenWindowWidth);
  }

  listenWindowWidth() {
    this.showFilterByWindowWidth();
  }

  showFilterByWindowWidth() {
    const w = window.innerWidth;
    if (w < 641) {
      this.showFilter = false;
    } else {
      this.showFilter = true;
    }
  }

  beforeDestroy() {
    // NOTE: Unmount sroll bottom to fetch data
    window.removeEventListener('resize', this.listenWindowWidth);
    window.removeEventListener('scroll', this.scrollBottom);
  }

  async firstFetch() {
    this.isLoadingSubject = true;
    this.isLoadingQuestions = true;

    await this.subjectStore.fetchSubject({
      subjectUuid: this.subjectUuid,
      subjectType: this.subjectType,
    });

    if (!this.subjectStore.error) {
      this.subject = this.subjectStore.subject;
      this.initializeFilters();
      this.questionFilters.languages = this.subject?.languages as string[];

      this.questionStore.pagination.page = 1;
      this.questionStore.pagination.isResetPage = true;
      this.selectedLanguages = this.questionFilters.languages;
      await this.questionStore.fetchQuestions({
        subjectUuid: this.subjectUuid,
        ...this.questionFilters,
      });
    }

    this.showFilterByWindowWidth();
    this.isLoadingSubject = false;
    this.isLoadingQuestions = false;
  }

  async refetchQuestions() {
    if (this.isLoadingQuestions) return;
    this.isLoadingQuestions = true;

    this.questionStore.pagination.page = 1;
    this.questionStore.pagination.isResetPage = true;

    this.selectedLanguages = this.questionFilters.languages;
    await this.questionStore.fetchQuestions({
      subjectUuid: this.subjectUuid,
      ...this.questionFilters,
    });
    this.isLoadingQuestions = false;
  }

  initializeFilters() {
    this.forms.forEach(form => {
      let availChapters = [...form.chapters];
      if (this.levelQuery) {
        availChapters = availChapters.filter(availChapter => {
          return availChapter.form === this.levelQuery;
        });
      }
      const chapterIds = availChapters.map(chapter => {
        return `${chapter.id}`;
      });

      this.questionFilters.chapterIds.push(...chapterIds);
    });

    if (this.initialChapters.length) {
      this.questionFilters.chapterIds = this.questionFilters.chapterIds.filter(
        chapterId => {
          return this.initialChapters.indexOf(chapterId) >= 0;
        },
      );
    }
  }

  async updateKeyword(param: string) {
    this.questionFilters.keyword = param;
    await this.refetchQuestions();
  }

  async updateFilters(param: QuestionFilters) {
    this.questionFilters = param;
    await this.refetchQuestions();
  }

  updateAllLoadingState(value: boolean) {
    this.isLoadingQuestions = value;
    this.isLoadingSubject = value;
    this.isLoadingAddQuestions = value;
  }

  async scrollBottom() {
    const { pagination } = this.questionStore;

    if (window.pageYOffset > window.innerHeight) {
      this.showMoveToTopButton = true;
    } else {
      this.showMoveToTopButton = false;
    }

    if (
      this.isLoadingQuestions ||
      this.isLoadingAddQuestions ||
      pagination.page >= pagination.totalPages
    ) {
      return;
    }

    const bottomOfWindow =
      Math.max(
        window.pageYOffset,
        document.documentElement.scrollTop,
        document.body.scrollTop,
      ) +
        window.innerHeight +
        200 >=
      document.documentElement.scrollHeight;

    if (!bottomOfWindow) {
      return;
    } else {
      this.questionStore.pagination.page += 1;

      this.isLoadingAddQuestions = true;

      this.selectedLanguages = this.questionFilters.languages;
      await this.questionStore.fetchQuestions({
        subjectUuid: this.subjectUuid,
        ...this.questionFilters,
      });
      this.isLoadingAddQuestions = false;
    }
  }

  openFilter() {
    if (this.isLoadingQuestions || this.isLoadingAddQuestions) return;
    this.showFilter = true;
  }

  closeFilter() {
    this.showFilter = false;
  }

  closePromoRegister() {
    this.showPromoRegister = false;
  }

  moveToTopClick() {
    window.scrollTo(0, 0);
  }

  viewOtherSubjectsClick() {
    this.$router.push({
      path: '/subjects',
    });
  }
}
